import React, { useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Layout, Menu, Avatar, Dropdown, Button, Tag, Space, theme } from 'antd'
import type { MenuProps } from 'antd'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  UserOutlined,
  CalendarOutlined,
  SettingOutlined,
  DatabaseOutlined,
  AuditOutlined,
  MessageOutlined,
  LineChartOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { useTenantConfig } from '@/contexts/TenantConfigContext'
import { filterMenu, MENU_DEFINITIONS } from './menuConfig'
import type { UserLevel } from '@/types'

const { Header, Sider, Content } = Layout

const ICON_MAP: Record<string, React.ReactNode> = {
  calendar:  <CalendarOutlined />,
  setting:   <SettingOutlined />,
  database:  <DatabaseOutlined />,
  audit:     <AuditOutlined />,
  message:   <MessageOutlined />,
  lineChart: <LineChartOutlined />,
}

function buildAntMenuItems(menus: ReturnType<typeof filterMenu>): MenuProps['items'] {
  return menus.map((m) => ({
    key: m.path,
    icon: m.icon ? ICON_MAP[m.icon] : undefined,
    label: m.children ? m.label : <Link to={m.path}>{m.label}</Link>,
    children: m.children ? buildAntMenuItems(m.children) : undefined,
  }))
}

const USER_LEVEL_COLORS: Record<number, string> = {
  0: 'red',
  4: 'blue',
  5: 'green',
  9: 'default',
}
const USER_LEVEL_LABELS: Record<number, string> = {
  0: '최고관리자',
  4: '기록담당',
  5: '조회담당',
  9: '일반',
}

const MainLayout: React.FC = () => {
  const { user, logout } = useAuth()
  const { siteOwner, modules, functions } = useTenantConfig()
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { token } = theme.useToken()

  const menuItems = useMemo(() => {
    if (!user) return []
    const filtered = filterMenu(MENU_DEFINITIONS, user.userLevel as UserLevel, modules, functions)
    return buildAntMenuItems(filtered)
  }, [user, modules, functions])

  // 현재 경로에 맞는 메뉴 키 계산
  const selectedKeys = useMemo(() => {
    const path = location.pathname
    return [path]
  }, [location.pathname])

  // 현재 경로의 상위 메뉴 펼침
  const openKeys = useMemo(() => {
    const path = location.pathname
    const parent = MENU_DEFINITIONS.find((m) =>
      m.children?.some((c) => path.startsWith(c.path)),
    )
    return parent ? [parent.path] : []
  }, [location.pathname])

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '로그아웃',
      onClick: () => logout().then(() => navigate('/login')),
    },
  ]

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={220}
        style={{ background: token.colorBgContainer, borderRight: `1px solid ${token.colorBorderSecondary}` }}
      >
        {/* 로고 */}
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? 0 : '0 16px',
            fontWeight: 700,
            fontSize: collapsed ? 18 : 15,
            color: token.colorPrimary,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {collapsed ? '⚾' : `⚾ ${siteOwner}`}
        </div>

        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys}
          items={menuItems}
          style={{ borderRight: 0, marginTop: 8 }}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>

      <Layout style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header
          style={{
            padding: '0 16px',
            background: token.colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />

          <Space>
            {user && (
              <Tag color={USER_LEVEL_COLORS[user.userLevel] ?? 'default'}>
                {USER_LEVEL_LABELS[user.userLevel] ?? `Level ${user.userLevel}`}
              </Tag>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" icon={<UserOutlined />}>
                {!collapsed && user?.name}
              </Button>
            </Dropdown>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Avatar style={{ cursor: 'pointer', background: token.colorPrimary }}>
                {user?.name?.charAt(0)}
              </Avatar>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: 24, background: token.colorBgLayout, flex: 1, overflow: 'hidden', height: 0 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
